// Conditions (step 5, Only when…): a "+ Time", "+ Network" and "+ Privilege"
// button each (not Privilege on Database), each condition once. Every
// condition is one summary row with its icon, Edit and remove; editing opens
// the condition's editor inline under the list. Stored conditions the console
// doesn't offer (Country, Risk, a one-time window, device...) are read-only
// rows with remove only, kept on save unless removed.
import React, { useState } from "react";
import { Button, Drawer, Radio, Select, TimePicker } from "antd";
import { Activity, Clock, Flag, Globe, KeyRound, Plus, SlidersHorizontal, X } from "lucide-react";
import dayjs from "dayjs";

import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import {
  COUNTRY_OPTIONS,
  conditionRows,
  detectTimezone,
  isValidNetworkEntry,
  normaliseNetworkEntry,
  removeCondition,
  timezoneOptions,
  FRIENDLY_ERRORS,
} from "../../../components/policyShell/conditionsModel";
import {
  COUNTRY_INTERNAL,
  COUNTRY_MEANING,
  DAY_OPTIONS,
  HOW_CONDITIONS_WORK,
  OFFERED_CONDITIONS,
  PRIVILEGE_MEANING,
  PRIVILEGE_OPTIONS,
  RISK_MEANING,
  RISK_OPTIONS,
  WEEKDAYS,
  offeredConditions,
} from "../../../components/policyShell/signals";
import { AddButton, Chips, ErrorText, Field, Group, HintText, Muted, TextButton, TokenInput } from "../ui";
import { fieldDomId } from "./draft";

// What a condition holds when it is added.
export const CONDITION_DEFAULTS = {
  time: () => ({ days: [...WEEKDAYS], start: "09:00", end: "18:00", timezone: detectTimezone(), outside: false }),
  network: () => ({ include: [], exclude: [] }),
  privilege: () => ({ levels: ["admin"] }),
  country: () => ({ include: [], exclude: [] }),
  risk: () => ({ levels: ["high"] }),
};

const clockToDayjs = (s) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || "").trim());
  return m ? dayjs().hour(Number(m[1])).minute(Number(m[2])).second(0) : null;
};

function TimeEditor({ value, onChange }) {
  const t = value || {};
  const patch = (fields) => onChange({ ...t, ...fields });
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Field label="Days" hint="none = every day">
        <Chips options={DAY_OPTIONS} value={t.days} onChange={(v) => patch({ days: v })} ariaLabel="Days" />
      </Field>
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <Field label="From">
          <TimePicker format="HH:mm" minuteStep={5} needConfirm={false} value={clockToDayjs(t.start)} onChange={(d) => patch({ start: d ? d.format("HH:mm") : "" })} aria-label="From" />
        </Field>
        <Field label="To">
          <TimePicker format="HH:mm" minuteStep={5} needConfirm={false} value={clockToDayjs(t.end)} onChange={(d) => patch({ end: d ? d.format("HH:mm") : "" })} aria-label="To" />
        </Field>
      </div>
      <Radio.Group value={t.outside ? "outside" : "inside"} onChange={(e) => patch({ outside: e.target.value === "outside" })} aria-label="Inside or outside these hours">
        <Radio value="inside">Inside these hours</Radio>
        <Radio value="outside">Outside these hours</Radio>
      </Radio.Group>
      <Field label="Time zone">
        <Select
          showSearch
          aria-label="Time zone"
          value={t.timezone || "UTC"}
          onChange={(v) => patch({ timezone: v })}
          options={timezoneOptions(t.timezone)}
          optionFilterProp="label"
          style={{ width: "100%", maxWidth: "22rem" }}
        />
      </Field>
    </div>
  );
}

function NetworkEditor({ value, onChange, note }) {
  const n = value || {};
  const validate = (v) => (isValidNetworkEntry(v) ? true : FRIENDLY_ERRORS.network);
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {note && <Muted>{note}</Muted>}
      <Field label="Only from" help="IPs or CIDRs. Empty means any address not listed under Never from.">
        <TokenInput ariaLabel="Only from" value={n.include} onChange={(v) => onChange({ ...n, include: v })} validate={validate} normalise={normaliseNetworkEntry} placeholder="10.0.0.0/8, 192.168.1.20" />
      </Field>
      <Field label="Never from">
        <TokenInput ariaLabel="Never from" value={n.exclude} onChange={(v) => onChange({ ...n, exclude: v })} validate={validate} normalise={normaliseNetworkEntry} placeholder="10.99.0.0/16" />
      </Field>
    </div>
  );
}

function CountryEditor({ value, onChange }) {
  const c = value || {};
  const picker = (key, label, help, placeholder) => (
    <Field label={label} help={help}>
      <Select
        mode="multiple"
        showSearch
        allowClear
        aria-label={label}
        value={c[key] || []}
        onChange={(v) => onChange({ ...c, [key]: v })}
        options={COUNTRY_OPTIONS}
        optionFilterProp="label"
        placeholder={placeholder}
        style={{ width: "100%" }}
      />
    </Field>
  );
  const named = [...(c.include || []), ...(c.exclude || [])];
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Muted>{COUNTRY_MEANING}</Muted>
      {picker("include", "Only from", "Empty means any country not listed under Not from.", "Any country")}
      {picker("exclude", "Not from", "For example India and Internal network: MFA only for people working remotely.", "None")}
      {named.length > 0 && !named.includes(COUNTRY_INTERNAL) && (
        <HintText>Logins from private addresses (office, VPN) are Internal network, not a country.</HintText>
      )}
    </div>
  );
}

function ConditionEditor({ kind, value, onChange, privilegeHint, networkNote }) {
  if (kind === "time") return <TimeEditor value={value} onChange={onChange} />;
  if (kind === "network") return <NetworkEditor value={value} onChange={onChange} note={networkNote} />;
  if (kind === "country") return <CountryEditor value={value} onChange={onChange} />;
  if (kind === "risk") {
    return (
      <Field label="Risk" help={RISK_MEANING}>
        <Chips options={RISK_OPTIONS} value={value?.levels} onChange={(v) => onChange({ levels: v })} ariaLabel="Risk" />
      </Field>
    );
  }
  return (
    <Field label="Privilege" help={privilegeHint}>
      <Chips options={PRIVILEGE_OPTIONS} value={value?.levels} onChange={(v) => onChange({ levels: v })} ariaLabel="Privilege" />
    </Field>
  );
}

const KIND_ICON = { time: Clock, network: Globe, privilege: KeyRound, country: Flag, risk: Activity };

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  padding: "0.625rem 0.75rem",
  border: `1px solid ${AN_COLORS.line}`,
  borderRadius: "0.625rem",
  background: AN_COLORS.bg,
};

export default function ConditionsSection({ adapter, draft, patchDraft, errors, allErrors = {}, touch }) {
  const [editing, setEditing] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const conditions = draft.conditions || {};
  const subject = adapter.labels?.subject || "sign-in";
  const rows = conditionRows(conditions, { subject, product: adapter.key });
  const offered = offeredConditions(adapter.key);
  const available = offered.filter((c) => !conditions[c.key]);
  const labelOf = (k) => OFFERED_CONDITIONS.find((c) => c.key === k)?.label || k;
  const privilegeHint = adapter.features?.privilegeHint ?? PRIVILEGE_MEANING[adapter.key] ?? "";

  const setConditions = (next) => patchDraft({ conditions: next && Object.keys(next).length ? next : undefined });
  const add = (key) => {
    setConditions({ ...conditions, [key]: CONDITION_DEFAULTS[key]() });
    setEditing(key);
  };
  const remove = (key) => {
    setConditions(removeCondition(conditions, key));
    if (editing === key) setEditing(null);
  };
  const done = (key) => {
    touch(`conditions.${key}`);
    if (!allErrors[`conditions.${key}`]) setEditing(null);
  };
  const editable = (key) => rows.some((r) => r.key === key && !r.hidden);

  return (
    <Group
      id="policy-advanced-conditions"
      title="Conditions"
      help={rows.length ? "The policy only applies when every condition holds." : "No conditions: it applies every time. Add one to narrow it."}
      aside={<TextButton onClick={() => setHelpOpen(true)}>How conditions work</TextButton>}
    >
      {rows.length > 0 && (
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {rows.map((r) => {
            const err = errors[`conditions.${r.key}`];
            const Icon = KIND_ICON[r.key] || SlidersHorizontal;
            return (
              <div
                key={r.key}
                id={fieldDomId(`conditions.${r.key}`)}
                style={{ ...rowStyle, scrollMarginTop: "1rem", borderColor: err && editing !== r.key ? AN_COLORS.danger : AN_COLORS.line }}
              >
                <span
                  aria-hidden
                  style={{
                    width: "1.75rem",
                    height: "1.75rem",
                    flexShrink: 0,
                    borderRadius: "0.5rem",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: AN_COLORS.surface,
                    border: `1px solid ${AN_COLORS.line}`,
                    color: r.hidden ? AN_COLORS.muted : AN_COLORS.primary,
                  }}
                >
                  <Icon size={14} />
                </span>
                <span style={{ width: "5rem", flexShrink: 0, fontSize: "0.8125rem", fontWeight: 600, color: r.hidden ? AN_COLORS.muted : AN_COLORS.ink }}>
                  {r.label}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: "0.875rem", color: r.hidden ? AN_COLORS.muted : AN_COLORS.ink2 }}>{r.hidden ? r.summary : r.summary || "Not set yet"}</span>
                  {editing !== r.key && <ErrorText>{err}</ErrorText>}
                </span>
                {!r.hidden && editing !== r.key && (
                  <TextButton onClick={() => setEditing(r.key)} aria-label={`Edit ${r.label}`}>
                    Edit
                  </TextButton>
                )}
                <button
                  type="button"
                  onClick={() => remove(r.key)}
                  aria-label={`Remove ${r.label}`}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: AN_COLORS.muted, padding: "0 0.25rem", display: "inline-flex", alignSelf: "center" }}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && conditions[editing] && editable(editing) && (
        <div
          style={{ border: `1px solid ${AN_COLORS.primary}`, borderRadius: "0.625rem", padding: "1rem 1.125rem", display: "grid", gap: "1rem", background: AN_COLORS.surface }}
          aria-label={`${labelOf(editing)} condition`}
          role="group"
        >
          <div style={{ fontSize: "0.875rem", fontWeight: 600, color: AN_COLORS.ink, fontFamily: AN_FONT }}>{labelOf(editing)}</div>
          <ConditionEditor
            kind={editing}
            value={conditions[editing]}
            onChange={(v) => setConditions({ ...conditions, [editing]: v })}
            privilegeHint={privilegeHint}
            networkNote={adapter.features?.networkNote}
          />
          <ErrorText>{errors[`conditions.${editing}`]}</ErrorText>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button size="small" type="primary" onClick={() => done(editing)}>
              Done
            </Button>
            <Button size="small" onClick={() => remove(editing)}>
              Remove
            </Button>
          </div>
        </div>
      )}

      {available.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {available.map((c) => (
            <AddButton key={c.key} onClick={() => add(c.key)} ariaLabel={`Add ${c.label} condition`}>
              <Plus size={14} aria-hidden />
              {c.label}
            </AddButton>
          ))}
        </div>
      )}

      <Drawer title="How conditions work" open={helpOpen} onClose={() => setHelpOpen(false)} width="24rem">
        {HOW_CONDITIONS_WORK.map((p) => (
          <p key={p.slice(0, 24)} style={{ fontSize: "0.875rem", color: AN_COLORS.ink2, lineHeight: 1.6, margin: "0 0 0.75rem" }}>
            {p}
          </p>
        ))}
      </Drawer>
    </Group>
  );
}
